import { FC } from 'react';
import { css } from 'styled-components';
import { Box, Button, Text, Lock, ProgressBar, RewardsStarGradient, RarePass, Skeleton, Tooltip, XP } from '../../../blocks';
import { ActvityType } from '../../../queries';
import { ActivityButton } from '../RewardsActivity/ActivityButton';
import { usePushWalletContext } from '@pushchain/ui-kit';
import { Image } from '../../../css/SharedStyling';

type BossQuestCardProps = {
  questId?: string;
  title: string;
  description: string;
  resetTime: any;
  progress?: number;
  maxProgress?: number;
  unlocks: {
    rarePass?: boolean;
    xp?: number;
  };
  isLocked: boolean;
  isLockedStatusLoading?: boolean;
  ctaText: string;
  icon?: string;
  image?: string;
  imageFullWidth?: boolean;
  onClaim?: () => void;
  ctaLink?: string;
  activityStatus?: any;
  isLoadingActivity?: boolean;
  refetchActivities?: () => void;
  userId?: string;
  completedMap?: Record<string, boolean>;
  setErrorMessage?: (errorMessage: string) => void;
};

const BossQuestCard: FC<BossQuestCardProps> = ({
  questId,
  title,
  description,
  resetTime,
  progress,
  maxProgress,
  unlocks,
  isLocked,
  isLockedStatusLoading = false,
  ctaText,
  icon,
  onClaim,
  activityStatus,
  isLoadingActivity,
  refetchActivities,
  userId,
  completedMap = {},
  setErrorMessage,
  image,
  imageFullWidth = false,
  ctaLink,
}) => {
  const showProgress = maxProgress > 0;
  const isCompleted = completedMap[questId] ?? true;

  const { universalAccount } = usePushWalletContext('wallet1');
  const isWalletConnected = Boolean(universalAccount?.address);

  return (
    <Box
      display="flex"
      flexDirection="column"
      width={{ initial: 'auto', tb: '100%' }}
      minWidth={{ initial: '280px', tb: 'auto' }}
      borderRadius="radius-md"
      padding="spacing-md"
      gap="spacing-sm"
      justifyContent="flex-end"
      position="relative"
      overflow="hidden"
      height={{ initial: '-webkit-fill-available', tb: 'auto' }}
      maxHeight={{ initial: '483px', tb: 'fit-content' }}
      minHeight={{ initial: '483px', tb: 'fit-content' }}
      css={css`
        border: 1px solid rgba(255, 255, 255, 0.10);
        background: radial-gradient(109.87% 94.08% at 50% 5.92%, #131220 75.21%, #291D39 100%);
        flex: 1;
        box-sizing: border-box;
      `}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignSelf="stretch"
        css={css`
          flex: 1;
        `}
      >
        {resetTime && <Box
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          alignSelf="flex-end"
          padding="spacing-xxs spacing-xs"
          borderRadius="radius-lg"
          css={css`
            background: rgba(66, 67, 103, 0.20);
            border: 1px solid rgba(255, 255, 255, 0.10);
          `}
        >
          <Text
            variant="bes-bold"
            css={css`
              color: #e5c4ef;
              text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.1);
              text-align: center;
            `}
          >
            Resets in {resetTime?.days}D {resetTime?.hours}H
          </Text>
        </Box>}

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          alignSelf="stretch"
          gap="spacing-lg"
          justifyContent="flex-end"
          css={css`
            flex: 1;
          `}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
            alignSelf="stretch"
            gap="spacing-xxs"
            height="100%"
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              alignSelf="stretch"
              gap="spacing-xs"
              css={css`
                margin-top: 10px;
                `}
            >
              <Box
                width="100%"
                >
                <Image src={image} alt=""
                  style={{
                    width: imageFullWidth ? '80%' : '69%',
                    height: 'auto',
                    margin: 'auto auto',
                    objectFit: 'contain'
                  }} />
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                alignSelf="stretch"
                gap="spacing-xxs"
                justifyContent="center"
              >

                <Text
                  variant="h4-semibold"
                  css={css`
                    background: linear-gradient(
                      180deg,
                      rgba(255, 255, 255, 1) 66%,
                      rgba(210, 83, 233, 1) 100%
                    );
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                    text-align: center;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  {title}
                </Text>

                <Text
                  variant="bm-regular"
                  css={css`
                    color: rgba(255, 255, 255, 0.75);
                    text-align: center;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  `}
                >
                  {description}
                </Text>
              </Box>
            </Box>

            {icon && (
              <ProgressBar
                progress={progress || 0 || null}
                max={maxProgress}
                size="large"
                progressIcon={
                  userId && !isLocked ? (
                    <Tooltip title={`${Math.round(((progress || 0) / (maxProgress || 1)) * 100)}%`}>
                      <Box display="flex"><RewardsStarGradient size={35} /></Box>
                    </Tooltip>
                  ) : (
                    <Box display="flex"><RewardsStarGradient size={35} /></Box>
                  )
                }
              />
            )}

            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              alignSelf="stretch"
              gap="spacing-xxs"
            >
              <Text
                variant="ol-bold"
                css={css`
                  color: rgba(255, 255, 255, 0.75);
                  text-align: center;
                  width: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                UNLOCKS
              </Text>

              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                gap="spacing-lg"
              >
                {unlocks.rarePass && (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    gap="spacing-xs"
                  >
                    <Box
                      display="inline-flex"
                      alignItems="center"
                      gap="spacing-xxs"
                    >

                      <RarePass />
                    </Box>
                  </Box>
                )}

                {unlocks.xp && (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    gap="spacing-xxs"
                    height="14px"
                  >
                    <XP />

                    <Text variant="bm-bold" color="text-primary">
                      {unlocks.xp}
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            display="flex"
            alignItems="flex-start"
            alignSelf="stretch"
            width="100%"
          >
            <Skeleton isLoading={isWalletConnected && isLockedStatusLoading} width="100%" height="32px" borderRadius="radius-xs">
              {!isLocked && questId && userId && (
                showProgress && progress < maxProgress ? (
                  <Button
                    variant="outline"
                    size="small"
                    disabled
                    css={css`
                      width: 100%;
                      background: transparent;
                    `}
                  >
                    Claim
                  </Button>
                ) : (
                  <ActivityButton
                    activityType={questId as ActvityType}
                    activityTypeId={questId}
                    userId={userId}
                    refetchActivity={() => refetchActivities?.()}
                    usersSingleActivity={activityStatus?.[questId]}
                    setErrorMessage={setErrorMessage}
                    isLoadingActivity={isLoadingActivity || false}
                    label="Claim"
                    buttonSize="small"
                    buttonVariant="primary"
                    buttonCss={css`
                      width: 100%;
                    `}
                  />
                )
            )}

            {(isLocked) && (
              <Button
                variant="outline"
                size="small"
                leadingIcon={< Lock />}
                css={css`
                  width: 100%;
                  `}
                disabled
              >
                Locked
              </Button>
            )}

            {!questId && !isLocked && (
              <a
                href={ctaLink || '#'}
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: '100%', textDecoration: 'none' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  padding="spacing-xxs spacing-sm"
                  borderRadius="radius-xs"
                  css={css`
                    width: 100%;
                    border: 1px solid #774d84;
                    background: none;
                    box-sizing: border-box;
                    cursor: pointer;
                  `}
                >
                  <Text
                    css={css`
                      font-family: "FK Grotesk Neue-Medium", Helvetica;
                      font-size: 14px;
                      font-weight: 500;
                      line-height: 16px;
                      white-space: nowrap;
                    `}
                  >
                    <Text as="span" color="text-primary">{ctaText.split(' on ')[0]} on </Text>
                    <Text as="span" css={css`
                        color: #c742dd;
                      `}>{ctaText.split(' on ')[1] || ''}</Text>
                  </Text>
                </Box>
              </a>
            )}
            </Skeleton>
          </Box>
        </Box>
      </Box>

      {showProgress && (
        <Box
          position="absolute"
          width="300px"
          height="8px"
          css={css`
              left: 0;
              top: 148px;
            `
          }
        >
          <Box width="1px" height="1px" />
        </Box>
      )}
    </Box>
  );
};

export default BossQuestCard;
